/**
 * Validators and shared helpers for floor-shape add forms.
 * Shape components (e.g. `bar/upp-bar.ts`) import {@link UppShapeUtils} from here to avoid cycles with {@link UppFloorShapeAddHostComponent}.
 */
import { AbstractControl, ValidationErrors } from '@angular/forms';
import type { FormGroup } from '@angular/forms';

import type { UppKonvaShapeKind } from '@unpispas/upp-konva';
import type { UppKonvaShapeSource } from '@unpispas/upp-konva';

/**
 * Structural type only: avoids pulling `@unpispas/upp-data` into this barrel (Jest init / cycles with floor edit).
 * Bounds **1..99** must match `QRCODE` chair limits in `@unpispas/upp-data`.
 */
export type QrCodeFromDrawTarget = {
    type?: string | null;
    custom?: string | null;
    /** Mismo contrato que `DrawItem.qrcode` (mesa enlazada al `DRAWITEM`). */
    qrcode?: { chairs: number | null } | null;
};

/** Form add scope; distinguishes `DOUBLE` for window vs stairs; `TABLE` for table variants. */
export type UppFloorShapeVariantScope =
    | 'BAR'
    | 'DOOR'
    | 'WINDOW'
    | 'STAIRS'
    | 'SOFA'
    | 'GENERIC'
    | 'TABLE';

/**
 * Pure helpers for floor Konva shape forms (palette slot, default row, validators, reads).
 * Same usage model as **`GenericUtils`** in `@unpispas/upp-defs`: **do not instantiate**; call `public static` methods only.
 */
export class UppShapeUtils {
    /**
     * Resolves the i18n key (`tr`) for a **variant** label (`data.shape` on `UppKonvaShapeSource`).
     *
     * @param scope Shape family (window vs stairs share `DOUBLE` with different copy).
     * @param modelValue Model value, e.g. `LINEAR`, `TOLEFT` (uppercased).
     */
    public static VariantTrId(scope: UppFloorShapeVariantScope, modelValue: string): string {
        const u = String(modelValue).toUpperCase();
        if (u === 'DOUBLE') {
            if (scope === 'STAIRS') {
                return '@place_edit_floor_shape_val_STAIRS_DOUBLE';
            }
            if (scope === 'WINDOW') {
                return '@place_edit_floor_shape_val_WINDOW_DOUBLE';
            }
        }
        if (scope === 'TABLE') {
            if (u === 'CIRCLE') {
                return '@place_edit_floor_shape_val_CIRCLE';
            }
            if (u === 'SQUARE') {
                return '@place_edit_floor_shape_val_SQUARE';
            }
        }
        return `@place_edit_floor_shape_val_${u}`;
    }

    /**
     * Suggested palette position (cm) for a new shape on the floor plan.
     *
     * @param index Shape index in the session (0-based); lays out a 4-column grid.
     */
    public static NewShapePaletteSlot(index: number): { x: number; y: number } {
        const col = index % 4;
        const row = Math.floor(index / 4);
        return { x: 32 + col * 96, y: 28 + row * 68 };
    }

    /**
     * Default Konva row when picking a kind in the quick selector (same base values as shape form `ngOnInit`).
     */
    public static DefaultShapeSource(kind: UppKonvaShapeKind, slot: { x: number; y: number }): UppKonvaShapeSource {
        const x = Math.round(slot.x);
        const y = Math.round(slot.y);
        switch (kind) {
            case 'BAR':
                return {
                    kind: 'BAR',
                    x,
                    y,
                    width: 168,
                    height: 52,
                    rotation: 0,
                    data: { shape: 'LINEAR' },
                };
            case 'DOOR':
                return {
                    kind: 'DOOR',
                    x,
                    y,
                    width: 72,
                    height: 14,
                    rotation: 0,
                    data: { shape: 'TOLEFT' },
                };
            case 'WINDOW':
                return {
                    kind: 'WINDOW',
                    x,
                    y,
                    width: 88,
                    height: 12,
                    rotation: 0,
                    data: { shape: 'SINGLE' },
                };
            case 'STAIRS':
                return {
                    kind: 'STAIRS',
                    x,
                    y,
                    width: 96,
                    height: 44,
                    rotation: 0,
                    data: { shape: 'SIMPLE' },
                };
            case 'TABLE':
                return {
                    kind: 'TABLE',
                    x,
                    y,
                    width: 120,
                    height: 80,
                    rotation: 0,
                    data: { shape: 'SQUARE', chairs: 4 },
                };
            case 'BILLIARD':
                return {
                    kind: 'BILLIARD',
                    x,
                    y,
                    width: 200,
                    height: 110,
                    rotation: 0,
                };
            case 'SOFA':
                return {
                    kind: 'SOFA',
                    x,
                    y,
                    width: 132,
                    height: 56,
                    rotation: 0,
                    data: { shape: 'LINEAR' },
                };
            case 'FOOTBALL':
                return {
                    kind: 'FOOTBALL',
                    x,
                    y,
                    width: 176,
                    height: 96,
                    rotation: 0,
                };
            case 'GENERIC':
                return {
                    kind: 'GENERIC',
                    x,
                    y,
                    width: 64,
                    height: 64,
                    rotation: 0,
                    data: { shape: 'SQUARE' },
                };
            case 'VERTEX':
                throw new Error('UppShapeUtils.DefaultShapeSource: VERTEX is not used in the furniture picker');
            default: {
                const _exhaustive: never = kind;
                return _exhaustive;
            }
        }
    }

    /** Validator: finite number **> 0** (dimensions in cm). */
    public static ShapeCmPositive(control: AbstractControl): ValidationErrors | null {
        const raw = control.value;
        if (raw === '' || raw === null || raw === undefined) {
            return { required: true };
        }
        const n = typeof raw === 'number' ? raw : parseFloat(String(raw));
        if (!Number.isFinite(n) || n <= 0) {
            return { dimension: true };
        }
        return null;
    }

    /** Validator: finite number **≥ 0** (position in cm). */
    public static ShapeCoordCmGeZero(control: AbstractControl): ValidationErrors | null {
        const raw = control.value;
        if (raw === '' || raw === null || raw === undefined) {
            return { required: true };
        }
        const n = typeof raw === 'number' ? raw : parseFloat(String(raw));
        if (!Number.isFinite(n) || n < 0) {
            return { dimension: true };
        }
        return null;
    }

    /** Validator: integer **≥ 0** (chair count, etc.). */
    public static ShapeNonNegativeInt(control: AbstractControl): ValidationErrors | null {
        const raw = control.value;
        if (raw === '' || raw === null || raw === undefined) {
            return { required: true };
        }
        const n = typeof raw === 'number' ? raw : parseFloat(String(raw));
        if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
            return { chairs: true };
        }
        return null;
    }

    public static ShapeStrictInteger(control: AbstractControl): ValidationErrors | null {
        const raw = control.value;
        if (raw === '' || raw === null || raw === undefined) {
            return null;
        }
        const n = typeof raw === 'number' ? raw : parseFloat(String(raw));
        if (!Number.isFinite(n) || !Number.isInteger(n)) {
            return { chairs: true };
        }
        return null;
    }

    /** Reads a numeric control from the group and returns rounded cm (0 if not finite). */
    public static ReadCm(g: FormGroup, key: string): number {
        const v = g.get(key)?.value;
        const n = typeof v === 'number' ? v : parseFloat(String(v));
        return Number.isFinite(n) ? Math.round(n) : 0;
    }

    /** Reads a numeric control; if not finite, returns `fallback`. */
    public static ReadNum(g: FormGroup, key: string, fallback = 0): number {
        const v = g.get(key)?.value;
        const n = typeof v === 'number' ? v : parseFloat(String(v));
        return Number.isFinite(n) ? n : fallback;
    }

    /**
     * Floor-plan contract: angle in **[0, 360)** degrees (Konva can output negatives or >360).
     */
    public static Rotation360(deg: number): number {
        if (!Number.isFinite(deg)) {
            return 0;
        }
        let x = deg % 360;
        if (x < 0) {
            x += 360;
        }
        return x === 0 ? 0 : x;
    }

    /** Reads form rotation and normalizes it to **[0, 360)** degrees. */
    public static ReadRotationDeg360(g: FormGroup, key = 'rotation'): number {
        return UppShapeUtils.Rotation360(UppShapeUtils.ReadNum(g, key, 0));
    }

    /**
     * Si `DRAWITEM.custom` incluye `chairs`, lo asigna a `draw.qrcode.chairs` cuando existe.
     * Los llamadores deben filtrar `TABLE`; hay guard defensivo de tipo.
     */
    public static QRCodeFromDraw(draw: QrCodeFromDrawTarget): void {
        const minC = 1;
        const maxC = 99;
        if (String(draw.type ?? '').trim().toUpperCase() !== 'TABLE') {
            return;
        }

        const raw = draw.custom;
        if (typeof raw !== 'string' || raw.trim() === '') {
            return;
        }

        let chairs: number | null = null;
        try {
            const o = JSON.parse(raw) as unknown;
            if (o && typeof o === 'object' && !Array.isArray(o) && 'chairs' in o) {
                const c = (o as { chairs?: unknown }).chairs;
                const n = typeof c === 'number' ? c : parseInt(String(c), 10);
                if (Number.isFinite(n)) {
                    chairs = Math.round(n);
                }
            }
        } catch {
            return;
        }

        if (chairs == null || chairs < minC || chairs > maxC) {
            return;
        }

        const qr = draw.qrcode;
        if (qr) {
            qr.chairs = chairs;
        }
    }
}
